import { KeyboardEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
    Autocomplete, Box, Button, Card, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton,
    InputBase, Stack, Switch, TextField, Tooltip, Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
    AddCircleOutline, CheckCircleOutline, ChevronLeft, ChevronRight, Close, ContentCopy, DeleteOutline, EditNote, ErrorOutline, Today,
} from "@mui/icons-material";
import { getData, postData } from "../../services/base.service";
import { useNotification } from "../Shared/NotificationProvider";
import { panelSx } from "../projects/projectUtils";
import {
    addDays, CATEGORY_META, CATEGORY_ORDER, dayLabel, formatHours, LONG_DAY_HOURS, longDate, mondayOf, otherLabel, parseHours,
    rowFromActivity, rowFromEntry, rowFromOther, rowFromProject, rowKey, rfsDisplay, sameRfsName, TimesheetCategory, TimesheetEntry,
    TimesheetOtherCategory, TimesheetProjectOption, TimesheetRfsOption, TimesheetRow, todayIso, weekDays, weekLabel,
} from "./timesheetUtils";

const WEEKEND_STORAGE_KEY = 'timesheet.showWeekend';

const readWeekendSetting = (): boolean => {
    try {
        return localStorage.getItem(WEEKEND_STORAGE_KEY) === 'true';
    } catch {
        return false;
    }
};

type SaveState = 'idle' | 'saving' | 'saved' | 'error';
const cellKey = (row: string, date: string) => `${row}|${date}`;

// ---------- one hour cell ----------

type HourCellProps = {
    rowIndex: number;
    colIndex: number;
    entry: TimesheetEntry | undefined;
    isToday: boolean;
    onCommit: (hours: number) => Promise<void>;
    // Gets the hours currently typed in the cell (may not be saved yet), null if invalid.
    onOpenDetails: (typedHours: number | null) => void;
};

function HourCell({ rowIndex, colIndex, entry, isToday, onCommit, onOpenDetails }: HourCellProps) {
    const theme = useTheme();
    const [text, setText] = useState(formatHours(entry?.hours ?? 0));
    const [invalid, setInvalid] = useState(false);
    const openDetails = () => onOpenDetails(parseHours(text));

    useEffect(() => { setText(formatHours(entry?.hours ?? 0)); setInvalid(false); }, [entry?.hours]);

    const commit = async () => {
        const hours = parseHours(text);
        if (hours === null) {
            setInvalid(true);
            return;
        }
        setInvalid(false);
        setText(formatHours(hours));
        if (hours !== (entry?.hours ?? 0)) await onCommit(hours);
    };

    const focusCell = (row: number, col: number) => {
        const target = document.querySelector<HTMLInputElement>(`input[data-ts-row="${row}"][data-ts-col="${col}"]`);
        target?.focus();
    };

    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) { openDetails(); return; }
        if (event.key === 'Enter' || event.key === 'ArrowDown') { event.preventDefault(); focusCell(rowIndex + 1, colIndex); }
        if (event.key === 'ArrowUp') { event.preventDefault(); focusCell(rowIndex - 1, colIndex); }
        if (event.key === 'ArrowRight' && event.currentTarget.selectionStart === text.length) focusCell(rowIndex, colIndex + 1);
        if (event.key === 'ArrowLeft' && event.currentTarget.selectionStart === 0) focusCell(rowIndex, colIndex - 1);
        if (event.key === 'Escape') { setText(formatHours(entry?.hours ?? 0)); setInvalid(false); }
    };

    return (
        <Box sx={{ position: 'relative', '&:hover .ts-note, &:focus-within .ts-note': { opacity: 1 } }}>
            <Tooltip title={invalid ? 'Use hours like 2, 2.5 or 2:30' : entry?.description || ''} placement="top" arrow>
                <InputBase
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onBlur={commit}
                    onKeyDown={onKeyDown}
                    onFocus={(e) => e.target.select()}
                    onDoubleClick={openDetails}
                    placeholder="–"
                    inputProps={{ 'data-ts-row': rowIndex, 'data-ts-col': colIndex, 'aria-label': 'Hours', style: { textAlign: 'center', padding: '6px 0' } }}
                    sx={{
                        width: '100%', borderRadius: 1.5, fontVariantNumeric: 'tabular-nums',
                        border: `1px solid ${invalid ? theme.palette.error.main : entry ? alpha(theme.palette.text.primary, 0.23) : alpha(theme.palette.text.primary, 0.1)}`,
                        backgroundColor: isToday ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
                        '&.Mui-focused': { borderColor: theme.palette.primary.main, boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}` },
                        '& input::placeholder': { color: theme.palette.text.disabled, opacity: 1 },
                    }}
                />
            </Tooltip>
            {entry?.description && (
                <Box sx={{ position: 'absolute', top: 4, right: 5, width: 5, height: 5, borderRadius: '50%', backgroundColor: 'primary.main', pointerEvents: 'none' }} />
            )}
            <IconButton className="ts-note" size="small" onClick={openDetails} aria-label="Edit time and description" tabIndex={-1}
                sx={{ position: 'absolute', right: -2, bottom: -2, padding: '1px', opacity: 0, transition: 'opacity 0.15s', color: 'text.secondary' }}>
                <EditNote sx={{ fontSize: 15 }} />
            </IconButton>
        </Box>
    );
}

// ---------- "Edit time" dialog ----------

// hours = what the cell shows when the dialog opens (typed hours are saved on blur, possibly still in flight).
type EditTarget = { row: TimesheetRow; date: string; entry: TimesheetEntry | undefined; hours: number };

function EditTimeDialog({ target, savedEntry, onClose, onSave }: {
    target: EditTarget | null; savedEntry: TimesheetEntry | undefined; onClose: () => void; onSave: (hours: number, description: string) => Promise<void>;
}) {
    const [hoursText, setHoursText] = useState('');
    const [description, setDescription] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (target) {
            setHoursText(formatHours(target.hours));
            setDescription(target.entry?.description ?? '');
            setError('');
        }
    }, [target]);

    const save = async () => {
        const hours = parseHours(hoursText);
        if (hours === null || hours === 0) {
            setError('Enter the hours, e.g. 2, 2.5 or 2:30');
            return;
        }
        await onSave(hours, description);
    };

    return (
        <Dialog open={!!target} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ paddingRight: 6 }}>
                Edit time
                <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} aria-label="Close"><Close /></IconButton>
            </DialogTitle>
            {target && (
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">{longDate(target.date)}</Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{target.row.title}</Typography>
                    {target.row.subtitle && <Typography variant="body2" color="text.secondary">{target.row.subtitle}</Typography>}
                    <TextField label="Hours" value={hoursText} autoFocus onChange={(e) => { setHoursText(e.target.value); setError(''); }}
                        error={!!error} helperText={error || '2, 2.5 or 2:30'} sx={{ marginTop: 2.5, width: 160 }}
                        onKeyDown={(e) => { if (e.key === 'Enter') save(); }} />
                    <TextField label="What did you work on?" value={description} onChange={(e) => setDescription(e.target.value)}
                        fullWidth multiline minRows={3} inputProps={{ maxLength: 1000 }} sx={{ marginTop: 2 }} />
                </DialogContent>
            )}
            <DialogActions sx={{ justifyContent: 'space-between', paddingX: 3, paddingBottom: 2 }}>
                {savedEntry ? (
                    <Button color="error" startIcon={<DeleteOutline />} onClick={() => onSave(0, '')}>Delete</Button>
                ) : <span />}
                <Stack direction="row" gap={1}>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button variant="contained" disableElevation onClick={save}>Save</Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
}

// ---------- "Add row" dialog ----------

// Coloured category chip: green implementation, red acquisition, blue backstopping, grey others / free text.
export function CategoryChip({ category, selected, onClick, size = 'medium' }: {
    category: TimesheetCategory; selected?: boolean; onClick?: () => void; size?: 'small' | 'medium';
}) {
    const meta = CATEGORY_META[category];
    const chip = (
        <Chip
            size={size}
            label={size === 'small' ? meta.short : meta.label}
            onClick={meta.disabled ? undefined : onClick}
            disabled={!!meta.disabled}
            variant={selected ? 'filled' : 'outlined'}
            sx={{
                borderColor: meta.color, color: selected ? '#fff' : meta.color, backgroundColor: selected ? meta.color : 'transparent',
                fontWeight: 600, '&:hover': onClick && !meta.disabled ? { backgroundColor: selected ? meta.color : alpha(meta.color, 0.08) } : undefined,
            }}
        />
    );
    return meta.disabled ? <Tooltip title={meta.disabled}><span>{chip}</span></Tooltip> : chip;
}

function AddRowDialog({ open, onClose, onAdd, onRfsCreated, onOtherCreated, projects, others }: {
    open: boolean; onClose: () => void; onAdd: (row: TimesheetRow) => void;
    onRfsCreated: (projectId: number, rfs: TimesheetRfsOption) => void; onOtherCreated: (other: TimesheetOtherCategory) => void;
    projects: TimesheetProjectOption[]; others: TimesheetOtherCategory[];
}) {
    const notify = useNotification();
    const [category, setCategory] = useState<TimesheetCategory>('implementation');
    const [project, setProject] = useState<TimesheetProjectOption | null>(null);
    const [rfs, setRfs] = useState<TimesheetRfsOption | null>(null);
    const [creatingRfs, setCreatingRfs] = useState(false);
    const [newRfsName, setNewRfsName] = useState('');
    const [newRfsCode, setNewRfsCode] = useState('');
    const [other, setOther] = useState<TimesheetOtherCategory | null>(null);
    const [creatingOther, setCreatingOther] = useState(false);
    const [newOtherName, setNewOtherName] = useState('');
    const [newOtherCode, setNewOtherCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [activity, setActivity] = useState('');

    const resetRfs = () => { setRfs(null); setCreatingRfs(false); setNewRfsName(''); setNewRfsCode(''); };
    const resetOther = () => { setOther(null); setCreatingOther(false); setNewOtherName(''); setNewOtherCode(''); };
    useEffect(() => {
        if (open) { setCategory('implementation'); setProject(null); resetRfs(); resetOther(); setActivity(''); }
    }, [open]);

    const isProject = CATEGORY_META[category].project;
    // On projects with RfS tracking the RfS is optional; staff can also create a new one here.
    const showRfs = isProject && !!project?.rfsTracking;
    // Names are unique - an existing one is selected instead of created twice.
    const duplicateRfs = creatingRfs && project ? project.rfs.find((r) => sameRfsName(r.name, newRfsName)) ?? null : null;
    const duplicateOther = creatingOther
        ? others.find((o) => sameRfsName(o.name, newOtherName) || (!!newOtherCode.trim() && o.code === newOtherCode.trim())) ?? null : null;

    const canAdd = isProject ? !!project && (!creatingRfs || newRfsName.trim().length > 0)
        : category === 'other' ? (creatingOther ? newOtherName.trim().length > 0 : !!other)
        : activity.trim().length > 0;

    const add = async () => {
        if (!canAdd || busy) return;
        if (category === 'free_text') { onAdd(rowFromActivity(activity)); return; }
        if (category === 'other') {
            if (!creatingOther) { onAdd(rowFromOther(other!)); return; }
            if (duplicateOther) {
                notify(`"${otherLabel(duplicateOther.code, duplicateOther.name)}" already exists - it is selected for you.`, 'warning');
                resetOther();
                setOther(duplicateOther);
                return;
            }
            setBusy(true);
            try {
                const created = await postData('timesheets/other-categories', { name: newOtherName.trim(), code: newOtherCode.trim() || null }) as
                    TimesheetOtherCategory & { created: boolean };
                const option = { id: created.id, code: created.code, name: created.name };
                onOtherCreated(option);
                if (!created.created) notify(`"${otherLabel(option.code, option.name)}" already exists - using it.`, 'warning');
                onAdd(rowFromOther(option));
            } catch (error) {
                notify(`Couldn't add the position: ${(error as Error).message}`, 'error');
            } finally {
                setBusy(false);
            }
            return;
        }
        if (!creatingRfs) { onAdd(rowFromProject(project!, category, rfs)); return; }
        if (duplicateRfs) {
            notify(`An RfS named "${duplicateRfs.name}" already exists on this project - it is selected for you.`, 'warning');
            setCreatingRfs(false);
            setNewRfsName('');
            setRfs(duplicateRfs);
            return;
        }
        setBusy(true);
        try {
            const created = await postData('timesheets/rfs', { projectId: project!.id, name: newRfsName.trim(), code: newRfsCode.trim() || undefined }) as TimesheetRfsOption & { created: boolean };
            const option = { id: created.id, name: created.name, code: created.code ?? null };
            onRfsCreated(project!.id, option);
            // someone else may have created the same name in the meantime
            if (!created.created) notify(`An RfS named "${option.name}" already exists on this project - using it.`, 'warning');
            onAdd(rowFromProject(project!, category, option));
        } catch (error) {
            notify(`Couldn't create the RfS: ${(error as Error).message}`, 'error');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Add row</DialogTitle>
            <DialogContent>
                <Stack direction="row" gap={1} flexWrap="wrap" sx={{ marginBottom: 2.5, marginTop: 0.5 }}>
                    {CATEGORY_ORDER.map((c) => (
                        <CategoryChip key={c} category={c} selected={category === c} onClick={() => setCategory(c)} />
                    ))}
                </Stack>
                {isProject && (
                    <Stack gap={2}>
                        <Autocomplete
                            options={projects}
                            value={project}
                            onChange={(_, value) => { setProject(value); resetRfs(); }}
                            groupBy={(option) => (option.mine ? 'Your projects' : 'All ongoing projects')}
                            getOptionLabel={(option) => `${option.projectNumber ?? ''} ${option.projectName ?? ''}${option.client ? ` · ${option.client}` : ''}`.trim()}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={(params) => <TextField {...params} autoFocus label="Ongoing project" placeholder="Search number, name or client" />}
                            noOptionsText="No ongoing project matches"
                        />
                        {showRfs && !creatingRfs && (
                            <Box>
                                <Autocomplete
                                    options={project!.rfs}
                                    value={rfs}
                                    onChange={(_, value) => setRfs(value)}
                                    getOptionLabel={(option) => rfsDisplay(option.name, option.code)}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    noOptionsText="No RfS yet - create one below"
                                    renderInput={(params) => <TextField {...params} label="RfS (optional)" placeholder="No specific RfS"
                                        helperText="This project tracks time per RfS. Pick one if your work belongs to an RfS - its name goes into the description." />}
                                />
                                <Button size="small" startIcon={<AddCircleOutline />} sx={{ marginTop: 0.5 }} onClick={() => { setRfs(null); setCreatingRfs(true); }}>
                                    RfS not in the list? Create a new one
                                </Button>
                            </Box>
                        )}
                        {showRfs && creatingRfs && (
                            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, padding: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>New RfS</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 1.5 }}>
                                    Give the RfS a short, descriptive name that colleagues will recognise, e.g. &quot;Workshop grid code Abuja&quot; or
                                    &quot;Feasibility study Lot 2&quot;. The RfS code is optional. Each name can only exist once per project.
                                </Typography>
                                <TextField size="small" fullWidth autoFocus label="RfS name" value={newRfsName} onChange={(e) => setNewRfsName(e.target.value)}
                                    inputProps={{ maxLength: 255 }} onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
                                    color={duplicateRfs ? 'warning' : undefined} focused={duplicateRfs ? true : undefined}
                                    helperText={duplicateRfs ? `"${duplicateRfs.name}" already exists on this project - it will be selected instead of creating it twice.` : ' '} />
                                <TextField size="small" fullWidth label="RfS code (optional)" value={newRfsCode} onChange={(e) => setNewRfsCode(e.target.value)}
                                    inputProps={{ maxLength: 50 }} onKeyDown={(e) => { if (e.key === 'Enter') add(); }} />
                                <Button size="small" sx={{ marginTop: 1 }} onClick={resetRfs}>Back to the RfS list</Button>
                            </Box>
                        )}
                    </Stack>
                )}
                {category === 'other' && !creatingOther && (
                    <Box>
                        <Autocomplete
                            options={others}
                            value={other}
                            onChange={(_, value) => setOther(value)}
                            getOptionLabel={(option) => otherLabel(option.code, option.name)}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={(params) => <TextField {...params} autoFocus label="Position" placeholder="e.g. 0100 General/Overarching, 9999 Holidays" />}
                        />
                        <Button size="small" startIcon={<AddCircleOutline />} sx={{ marginTop: 0.5 }} onClick={() => { setOther(null); setCreatingOther(true); }}>
                            Position not in the list? Add a new one
                        </Button>
                    </Box>
                )}
                {category === 'other' && creatingOther && (
                    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, padding: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>New position</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 1.5 }}>
                            For time that belongs to no project. The new position is available to everyone; the code is optional.
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                            <TextField size="small" label="Code (optional)" placeholder="e.g. 0500" value={newOtherCode} onChange={(e) => setNewOtherCode(e.target.value)}
                                inputProps={{ maxLength: 10 }} sx={{ width: { sm: 150 } }} />
                            <TextField size="small" autoFocus label="Name" value={newOtherName} onChange={(e) => setNewOtherName(e.target.value)} sx={{ flexGrow: 1 }}
                                inputProps={{ maxLength: 100 }} onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
                                color={duplicateOther ? 'warning' : undefined} focused={duplicateOther ? true : undefined}
                                helperText={duplicateOther ? `"${otherLabel(duplicateOther.code, duplicateOther.name)}" already exists - it will be selected instead.` : ' '} />
                        </Stack>
                        <Button size="small" onClick={resetOther}>Back to the list</Button>
                    </Box>
                )}
                {category === 'free_text' && (
                    <TextField fullWidth autoFocus label="Activity" placeholder="Internal: tender GIZ Kenya" value={activity}
                        onChange={(e) => setActivity(e.target.value)} inputProps={{ maxLength: 255 }}
                        onKeyDown={(e) => { if (e.key === 'Enter') add(); }} />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}>Cancel</Button>
                <Button variant="contained" disableElevation disabled={!canAdd || busy} onClick={add}>
                    {busy ? <CircularProgress size={18} />
                        : (creatingRfs && isProject && !duplicateRfs) ? 'Create RfS and add row'
                        : (creatingOther && category === 'other' && !duplicateOther) ? 'Add position and row' : 'Add row'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// What identifies a row for the API (save / remove).
const rowTarget = (row: TimesheetRow) => ({
    category: row.category,
    projectId: row.projectId ?? undefined,
    activity: row.activity ?? undefined,
    rfsId: row.rfsId ?? undefined,
    otherCategoryId: row.otherCategoryId ?? undefined,
});

// ---------- week grid ----------

function TimesheetWeek() {
    const theme = useTheme();
    const notify = useNotification();
    const [weekStart, setWeekStart] = useState(() => mondayOf(todayIso()));
    const [showWeekend, setShowWeekend] = useState(readWeekendSetting);
    const [entries, setEntries] = useState<Map<string, TimesheetEntry>>(new Map());
    const [extraRows, setExtraRows] = useState<TimesheetRow[]>([]);
    const [projects, setProjects] = useState<TimesheetProjectOption[]>([]);
    const [others, setOthers] = useState<TimesheetOtherCategory[]>([]);
    const [loading, setLoading] = useState(false);
    const [saveState, setSaveState] = useState<SaveState>('idle');
    const [addOpen, setAddOpen] = useState(false);
    const [editTarget, setEditTarget] = useState<EditTarget | null>(null);

    const days = useMemo(() => weekDays(weekStart, showWeekend), [weekStart, showWeekend]);
    const today = todayIso();

    const loadWeek = useCallback(async (start: string) => {
        setLoading(true);
        try {
            const result = await getData(`timesheets/week?start=${start}`) as { weekStart: string; entries: TimesheetEntry[] };
            const map = new Map<string, TimesheetEntry>();
            result.entries.forEach((entry) => map.set(cellKey(rowKey(entry), entry.date), entry));
            setEntries(map);
        } catch (error) {
            notify(`Couldn't load the week: ${(error as Error).message}`, 'error');
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => { setExtraRows([]); setSaveState('idle'); loadWeek(weekStart); }, [weekStart, loadWeek]);

    useEffect(() => {
        getData('timesheets/projects').then((res) => setProjects(res as TimesheetProjectOption[])).catch((error) => console.error(error));
        getData('timesheets/other-categories').then((res) => setOthers(res as TimesheetOtherCategory[])).catch((error) => console.error(error));
    }, []);

    const toggleWeekend = (value: boolean) => {
        setShowWeekend(value);
        try { localStorage.setItem(WEEKEND_STORAGE_KEY, String(value)); } catch { /* per-viewer convenience only */ }
    };

    // Rows: everything that has hours this week, plus rows added (or copied) but not filled yet.
    const rows = useMemo(() => {
        const result: TimesheetRow[] = [];
        const seen = new Set<string>();
        Array.from(entries.values())
            .sort((a, b) => CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category)
                || String(a.projectNumber ?? a.otherCategoryCode ?? a.activity).localeCompare(String(b.projectNumber ?? b.otherCategoryCode ?? b.activity), undefined, { numeric: true })
                || String(a.rfsName ?? '').localeCompare(String(b.rfsName ?? ''), undefined, { numeric: true }))
            .forEach((entry) => {
                const row = rowFromEntry(entry);
                if (!seen.has(row.key)) { seen.add(row.key); result.push(row); }
            });
        extraRows.forEach((row) => { if (!seen.has(row.key)) { seen.add(row.key); result.push(row); } });
        return result;
    }, [entries, extraRows]);

    const save = async (row: TimesheetRow, date: string, hours: number, description?: string) => {
        setSaveState('saving');
        try {
            const saved = await postData('timesheets/entry', {
                date, hours, ...rowTarget(row),
                ...(description !== undefined ? { description } : {}),
            }) as TimesheetEntry | { deleted: true };
            const kept = 'deleted' in saved ? null : saved;
            setEntries((prev) => {
                const next = new Map(prev);
                if (kept) next.set(cellKey(row.key, date), kept);
                else next.delete(cellKey(row.key, date));
                return next;
            });
            // keep the row visible after its last cell was cleared
            if (!kept) setExtraRows((prev) => (prev.some((r) => r.key === row.key) ? prev : [...prev, row]));
            setSaveState('saved');
        } catch (error) {
            setSaveState('error');
            notify(`Couldn't save: ${(error as Error).message}`, 'error');
        }
    };

    const addRow = (row: TimesheetRow) => {
        if (rows.some((r) => r.key === row.key)) {
            notify(`${row.title}${row.rfsId !== null ? ` (${row.subtitle})` : ''} is already in this week.`, 'info');
        } else {
            setExtraRows((prev) => [...prev, row]);
        }
        setAddOpen(false);
    };

    const copyLastWeek = async () => {
        try {
            const result = await getData(`timesheets/week?start=${addDays(weekStart, -7)}`) as { entries: TimesheetEntry[] };
            const existing = new Set(rows.map((r) => r.key));
            const toAdd: TimesheetRow[] = [];
            result.entries.forEach((entry) => {
                const row = rowFromEntry(entry);
                if (!existing.has(row.key)) { existing.add(row.key); toAdd.push(row); }
            });
            setExtraRows((prev) => [...prev, ...toAdd]);
            notify(toAdd.length ? `${toAdd.length} row(s) copied from last week - hours are not copied.` : 'Nothing new to copy from last week.', 'info');
        } catch (error) {
            notify(`Couldn't copy last week: ${(error as Error).message}`, 'error');
        }
    };

    const removeRow = async (row: TimesheetRow) => {
        const hasHours = Array.from(entries.keys()).some((key) => key.startsWith(`${row.key}|`));
        if (hasHours) {
            if (!window.confirm(`Remove ${row.title} and its hours from this week?`)) return;
            try {
                await postData('timesheets/row/remove', { weekStart, ...rowTarget(row) });
                setEntries((prev) => new Map(Array.from(prev.entries()).filter(([key]) => !key.startsWith(`${row.key}|`))));
            } catch (error) {
                notify(`Couldn't remove the row: ${(error as Error).message}`, 'error');
                return;
            }
        }
        setExtraRows((prev) => prev.filter((r) => r.key !== row.key));
    };

    const rowTotal = (row: TimesheetRow) => days.reduce((sum, date) => sum + (entries.get(cellKey(row.key, date))?.hours ?? 0), 0);
    const dayTotal = (date: string) => rows.reduce((sum, row) => sum + (entries.get(cellKey(row.key, date))?.hours ?? 0), 0);
    const weekTotal = days.reduce((sum, date) => sum + dayTotal(date), 0);
    // Hours booked on hidden weekend days still count in the row totals' tooltip.
    const hiddenWeekendHours = showWeekend ? 0 : Array.from(entries.values())
        .filter((e) => dayLabel(e.date).isWeekend).reduce((sum, e) => sum + e.hours, 0);

    const gridColumns = `minmax(220px, 2.6fr) repeat(${days.length}, minmax(64px, 1fr)) 76px 40px`;
    const headerCell = { paddingY: 1.25, paddingX: 0.75, color: 'text.secondary', fontSize: 13 } as const;

    return (
        <>
            <Card elevation={0} sx={{ ...panelSx(theme), overflow: 'hidden' }}>
                {/* Week navigation */}
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} gap={1.5}
                    sx={{ paddingX: 2, paddingY: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                    <Stack direction="row" alignItems="center" gap={1}>
                        <IconButton size="small" onClick={() => setWeekStart(addDays(weekStart, -7))} aria-label="Previous week"><ChevronLeft /></IconButton>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, minWidth: 230, textAlign: 'center' }}>{weekLabel(weekStart, showWeekend)}</Typography>
                        <IconButton size="small" onClick={() => setWeekStart(addDays(weekStart, 7))} aria-label="Next week"><ChevronRight /></IconButton>
                        {weekStart !== mondayOf(today) && (
                            <Button size="small" startIcon={<Today />} onClick={() => setWeekStart(mondayOf(today))}>This week</Button>
                        )}
                        <TextField type="date" size="small" value="" onChange={(e) => e.target.value && setWeekStart(mondayOf(e.target.value))}
                            inputProps={{ 'aria-label': 'Jump to a date' }} sx={{ width: 150, marginLeft: 1 }} helperText="" />
                        {loading && <CircularProgress size={18} />}
                    </Stack>
                    <FormControlLabel control={<Switch checked={showWeekend} onChange={(e) => toggleWeekend(e.target.checked)} />} label="Show weekend" />
                </Stack>

                <Box sx={{ overflowX: 'auto' }}>
                    <Box sx={{ minWidth: 640 }}>
                        {/* Header */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: gridColumns, backgroundColor: 'action.hover', alignItems: 'center' }}>
                            <Box sx={{ ...headerCell, paddingX: 2 }}>Project or activity</Box>
                            {days.map((date) => {
                                const label = dayLabel(date);
                                const isToday = date === today;
                                return (
                                    <Box key={date} sx={{ ...headerCell, textAlign: 'center', color: isToday ? 'primary.main' : label.isWeekend ? 'text.disabled' : 'text.secondary', fontWeight: isToday ? 600 : 400 }}>
                                        {label.weekday} {label.day}
                                    </Box>
                                );
                            })}
                            <Box sx={{ ...headerCell, textAlign: 'right' }}>Total</Box>
                            <Box />
                        </Box>

                        {/* Rows */}
                        {rows.length === 0 && !loading && (
                            <Box sx={{ paddingY: 5, textAlign: 'center' }}>
                                <Typography color="text.secondary">No time booked this week yet.</Typography>
                                <Typography variant="body2" color="text.secondary">Add a project row, or copy your rows from last week.</Typography>
                            </Box>
                        )}
                        {rows.map((row, rowIndex) => (
                            <Box key={row.key} sx={{ display: 'grid', gridTemplateColumns: gridColumns, alignItems: 'center', borderTop: 1, borderColor: 'divider',
                                '&:hover .ts-remove': { opacity: 1 } }}>
                                <Box sx={{ paddingX: 2, paddingY: 1, minWidth: 0, borderLeft: `4px solid ${CATEGORY_META[row.category].color}` }}>
                                    <Typography variant="body2" noWrap title={row.title} sx={{ fontWeight: 500 }}>{row.title}</Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{row.subtitle}</Typography>
                                </Box>
                                {days.map((date, colIndex) => (
                                    <Box key={date} sx={{ paddingX: 0.5, paddingY: 0.75 }}>
                                        <HourCell
                                            rowIndex={rowIndex}
                                            colIndex={colIndex}
                                            entry={entries.get(cellKey(row.key, date))}
                                            isToday={date === today}
                                            onCommit={(hours) => save(row, date, hours)}
                                            onOpenDetails={(typedHours) => {
                                                const entry = entries.get(cellKey(row.key, date));
                                                setEditTarget({ row, date, entry, hours: typedHours ?? entry?.hours ?? 0 });
                                            }}
                                        />
                                    </Box>
                                ))}
                                <Typography variant="body2" sx={{ textAlign: 'right', paddingX: 0.75, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                                    {formatHours(rowTotal(row), false)}
                                </Typography>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Tooltip title="Remove row">
                                        <IconButton className="ts-remove" size="small" onClick={() => removeRow(row)} sx={{ opacity: 0.35, transition: 'opacity 0.15s' }}>
                                            <Close fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                        ))}

                        {/* Add row */}
                        <Box sx={{ borderTop: 1, borderColor: 'divider', paddingX: 1, paddingY: 0.5 }}>
                            <Button startIcon={<AddCircleOutline />} onClick={() => setAddOpen(true)}>Select project</Button>
                        </Box>

                        {/* Totals */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: gridColumns, alignItems: 'center', borderTop: 2, borderColor: 'divider', backgroundColor: 'action.hover' }}>
                            <Typography variant="body2" sx={{ paddingX: 2, paddingY: 1.25, fontWeight: 600 }}>Total</Typography>
                            {days.map((date) => {
                                const total = dayTotal(date);
                                const long = total > LONG_DAY_HOURS;
                                return (
                                    <Tooltip key={date} title={long ? `More than ${LONG_DAY_HOURS} hours on this day` : ''}>
                                        <Typography variant="body2" sx={{ textAlign: 'center', fontVariantNumeric: 'tabular-nums', fontWeight: long ? 600 : 400,
                                            color: long ? 'warning.dark' : 'text.primary' }}>
                                            {formatHours(total, false)}
                                        </Typography>
                                    </Tooltip>
                                );
                            })}
                            <Tooltip title={hiddenWeekendHours ? `Plus ${formatHours(hiddenWeekendHours)} on the hidden weekend` : ''}>
                                <Typography variant="subtitle1" sx={{ textAlign: 'right', paddingX: 0.75, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                                    {formatHours(weekTotal, false)}{hiddenWeekendHours ? '*' : ''}
                                </Typography>
                            </Tooltip>
                            <Box />
                        </Box>
                    </Box>
                </Box>

                {/* Footer actions */}
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ paddingX: 2, paddingY: 1.5, borderTop: 1, borderColor: 'divider' }}>
                    <Button variant="outlined" size="small" startIcon={<AddCircleOutline />} onClick={() => setAddOpen(true)}>Add row</Button>
                    <Button variant="outlined" size="small" startIcon={<ContentCopy />} onClick={copyLastWeek}>Copy rows from last week</Button>
                    <Box sx={{ flexGrow: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                        Type 2, 2.5 or 2:30 · Enter moves down · double-click a cell to add a description
                    </Typography>
                    <Stack direction="row" alignItems="center" gap={0.5} sx={{ minWidth: 150, justifyContent: 'flex-end' }}>
                        {saveState === 'saving' && <><CircularProgress size={14} /><Typography variant="caption" color="text.secondary">Saving…</Typography></>}
                        {saveState === 'saved' && <><CheckCircleOutline sx={{ fontSize: 16, color: 'success.main' }} /><Typography variant="caption" color="text.secondary">Saved automatically</Typography></>}
                        {saveState === 'error' && <><ErrorOutline sx={{ fontSize: 16, color: 'error.main' }} /><Typography variant="caption" color="error">Not saved</Typography></>}
                    </Stack>
                </Stack>
            </Card>

            <AddRowDialog open={addOpen} onClose={() => setAddOpen(false)} onAdd={addRow} projects={projects} others={others}
                onOtherCreated={(other) => setOthers((prev) => (prev.some((o) => o.id === other.id) ? prev
                    : [...prev, other].sort((x, y) => (x.code ?? '\uffff').localeCompare(y.code ?? '\uffff') || x.name.localeCompare(y.name))))}
                onRfsCreated={(projectId, rfs) => setProjects((prev) => prev.map((p) => (p.id === projectId && !p.rfs.some((r) => r.id === rfs.id)
                    ? { ...p, rfs: [...p.rfs, rfs].sort((x, y) => x.name.localeCompare(y.name, undefined, { numeric: true })) } : p)))} />
            <EditTimeDialog
                target={editTarget}
                savedEntry={editTarget ? entries.get(cellKey(editTarget.row.key, editTarget.date)) : undefined}
                onClose={() => setEditTarget(null)}
                onSave={async (hours, description) => {
                    if (!editTarget) return;
                    await save(editTarget.row, editTarget.date, hours, description);
                    setEditTarget(null);
                }}
            />
        </>
    );
}

export default TimesheetWeek;
